import { dataroomClient, DOC_FIELD } from './client';
import type {
  AckResult,
  BankDetails,
  DeclarationResult,
  Party,
  PartiesResult,
  ThemeResponse,
  FormResult,
  OtpSent,
  AadhaarInitiated,
  SessionResponse,
  StatusResponse,
  SubmitResult,
  TasksResponse,
  UploadResult,
} from './types';

/**
 * The data-room API calls (PS-5 FROZEN CONTRACT). Responses are the literal bodies (no envelope).
 * Tenancy is server-bound: NONE of these calls send a tenant / hub / entity id — the session bearer
 * carries it. The client never asserts a tenant (contrast the legacy enclave's client-chosen header).
 */

const enc = (s: string) => encodeURIComponent(s);

/**
 * POST /v1/session {inviteToken} — exchange the single-use invite for a session. Sends NO bearer
 * (none exists yet). A 410 here means the invite is already used/expired (handled by the caller).
 */
export async function createSession(inviteToken: string): Promise<SessionResponse> {
  const { data } = await dataroomClient.post<SessionResponse>('/v1/session', { inviteToken });
  return data;
}

/** GET /v1/tasks — the applicant's task list + overall status. */
export async function getTasks(): Promise<TasksResponse> {
  const { data } = await dataroomClient.get<TasksResponse>('/v1/tasks');
  return data;
}

/**
 * POST /v1/tasks/{taskId}/documents — upload a document (multipart). 413/415 rejections surface as
 * NormalizedError kinds the caller renders kindly. `onProgress` is 0..100.
 */
export async function uploadDocument(
  taskId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadResult> {
  const form = new FormData();
  form.append(DOC_FIELD, file, file.name);
  const { data } = await dataroomClient.post<UploadResult>(
    `/v1/tasks/${enc(taskId)}/documents`,
    form,
    {
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        }
      },
    },
  );
  return data;
}

/** POST /v1/tasks/{taskId}/form {fields} — submit a completed form. */
export async function submitForm(
  taskId: string,
  fields: Record<string, string>,
): Promise<FormResult> {
  const { data } = await dataroomClient.post<FormResult>(`/v1/tasks/${enc(taskId)}/form`, {
    fields,
  });
  return data;
}

/** POST /v1/tasks/{taskId}/acknowledge {} — confirm the applicant has read/accepted. */
export async function acknowledgeTask(taskId: string): Promise<AckResult> {
  const { data } = await dataroomClient.post<AckResult>(
    `/v1/tasks/${enc(taskId)}/acknowledge`,
    {},
  );
  return data;
}

/**
 * POST /v1/tasks/{taskId}/declaration — accept a DECLARATION. Sends the version the applicant was
 * shown, never the text: the server hashes the text it resolved, and a stale version is a 409.
 */
export async function submitDeclaration(
  taskId: string,
  version: string,
): Promise<DeclarationResult> {
  const { data } = await dataroomClient.post<DeclarationResult>(
    `/v1/tasks/${enc(taskId)}/declaration`,
    { accepted: true, version },
  );
  return data;
}

/** POST /v1/tasks/{taskId}/parties {parties} — declare related parties (directors, UBOs, …). */
export async function submitParties(taskId: string, parties: Party[]): Promise<PartiesResult> {
  const { data } = await dataroomClient.post<PartiesResult>(`/v1/tasks/${enc(taskId)}/parties`, {
    parties,
  });
  return data;
}

/**
 * POST /v1/tasks/{taskId}/aadhaar/initiate — records consent and asks UIDAI for a code (OL-22 slice
 * 3). The number is sent once, in the body, and is never stored by the room.
 */
export async function initiateAadhaar(
  taskId: string,
  version: string,
  aadhaarNumber: string,
): Promise<AadhaarInitiated> {
  const { data } = await dataroomClient.post<AadhaarInitiated>(
    `/v1/tasks/${enc(taskId)}/aadhaar/initiate`,
    { accepted: true, version, aadhaarNumber },
  );
  return data;
}

/** POST /v1/tasks/{taskId}/aadhaar/verify {code} — the provider checks the code. */
export async function verifyAadhaar(taskId: string, code: string): Promise<SubmitResult> {
  const { data } = await dataroomClient.post<SubmitResult>(`/v1/tasks/${enc(taskId)}/aadhaar/verify`, {
    code,
  });
  return data;
}

/** POST /v1/tasks/{taskId}/otp/send {contact} — send a one-time code (OL-22 slice 2). */
export async function sendOtp(taskId: string, contact: string): Promise<OtpSent> {
  const { data } = await dataroomClient.post<OtpSent>(`/v1/tasks/${enc(taskId)}/otp/send`, {
    contact,
  });
  return data;
}

/** POST /v1/tasks/{taskId}/otp/verify {code} — the code is checked and spent server-side. */
export async function verifyOtp(taskId: string, code: string): Promise<SubmitResult> {
  const { data } = await dataroomClient.post<SubmitResult>(`/v1/tasks/${enc(taskId)}/otp/verify`, {
    code,
  });
  return data;
}

/** POST /v1/tasks/{taskId}/identifier {value} — format-checked only; no registry result returns. */
export async function submitIdentifier(taskId: string, value: string): Promise<SubmitResult> {
  const { data } = await dataroomClient.post<SubmitResult>(`/v1/tasks/${enc(taskId)}/identifier`, {
    value,
  });
  return data;
}

/** POST /v1/tasks/{taskId}/bank-details — account + IFSC, or a VPA. */
export async function submitBankDetails(taskId: string, body: BankDetails): Promise<SubmitResult> {
  const { data } = await dataroomClient.post<SubmitResult>(
    `/v1/tasks/${enc(taskId)}/bank-details`,
    body,
  );
  return data;
}

/** GET /v1/status — the friendly, tipping-off-safe progress view. */
export async function getStatus(): Promise<StatusResponse> {
  const { data } = await dataroomClient.get<StatusResponse>('/v1/status');
  return data;
}

/**
 * GET /v1/theme — the hub's live white-label theme (OPSFL-CLIENT-PLATFORM P1). Session-scoped:
 * the hub comes from the bearer, never a parameter. A failure here is NON-FATAL by contract —
 * the room falls back to its built-in identity.
 */
export async function getTheme(): Promise<ThemeResponse> {
  const { data } = await dataroomClient.get<ThemeResponse>('/v1/theme');
  return data;
}

/**
 * GET /v1/assets/{id} — a brand image or font. The bytes are session-scoped, so they come through this
 * client, with the bearer: an `<img src>` or a stylesheet could never send it. Nothing about the room's
 * CSP has to widen, as the bytes reach `blob:` for an image and `new FontFace(buffer)` for a font.
 */
export async function fetchAsset(assetId: string): Promise<{ bytes: ArrayBuffer; mediaType: string }> {
  const { data, headers } = await dataroomClient.get<ArrayBuffer>(`/v1/assets/${enc(assetId)}`, {
    responseType: 'arraybuffer',
  });
  return { bytes: data, mediaType: String(headers['content-type'] ?? '') };
}
